import { Dispatch, Reducer, useReducer } from 'react';
import {
  Action,
  APPLY,
  APPLY_ALL,
  CANCEL_DISMISS_ALL,
  CONFIRM_DISMISS_ALL,
  DISMISS,
  DISMISS_NO_RESULTS_ALERT,
  REQUEST_DISMISS_ALL,
  RESET_FOR_TRAVELER,
  RESET_REVIEW,
  SKIP_ENTRY_FOR_SESSION,
  START_REVIEW_BEGIN,
  START_REVIEW_ERROR,
  START_REVIEW_NO_RESULTS,
  START_REVIEW_SUCCESS,
  UNAPPLY,
  UNDISMISS,
} from './actions';
import { SuggestionsStateProps } from './types';
import {
  emptySuggestionsState,
  transitionSuggestion,
} from './utils';

const initialState: SuggestionsStateProps = {
  isReviewing: false,
  isLoadingSuggestions: false,
  validation: undefined,
  suggestions: emptySuggestionsState(),
  noResultsAlertOpen: false,
  isEntryAlertOpen: true,
  skippedForVisit: false,
  isDismissAllConfirmOpen: false,
};

const reducer: Reducer<SuggestionsStateProps, Action> = (state, action): SuggestionsStateProps => {
  switch (action.type) {
    case START_REVIEW_BEGIN:
      // Clears any prior review verdict, opens the loading state, and closes
      // the entry-alert banner so the page doesn't render two competing
      // surfaces while suggestions are in flight.
      return {
        ...state,
        isReviewing: true,
        isLoadingSuggestions: true,
        validation: undefined,
        noResultsAlertOpen: false,
        isEntryAlertOpen: false,
      };
    case START_REVIEW_SUCCESS:
      return {
        ...state,
        suggestions: action.suggestions,
        validation: action.validation,
        isLoadingSuggestions: false,
      };
    case START_REVIEW_NO_RESULTS:
      // Tool succeeded but produced nothing actionable. Surface the inline
      // info alert, exit review mode, and silence the entry alert for this
      // visit so the user isn't re-prompted with the same empty result.
      return {
        ...state,
        noResultsAlertOpen: true,
        isReviewing: false,
        isLoadingSuggestions: false,
        skippedForVisit: true,
      };
    case START_REVIEW_ERROR:
      return {
        ...state,
        isReviewing: false,
        isLoadingSuggestions: false,
      };
    case RESET_REVIEW:
      return {
        ...state,
        suggestions: emptySuggestionsState(),
        isReviewing: false,
        validation: undefined,
        isDismissAllConfirmOpen: false,
      };
    case APPLY:
      return {
        ...state,
        suggestions: transitionSuggestion(state.suggestions, action.locator, 'pending', 'accepted'),
      };
    case UNAPPLY:
      return {
        ...state,
        suggestions: transitionSuggestion(state.suggestions, action.locator, 'accepted', 'pending'),
      };
    case DISMISS:
      return {
        ...state,
        suggestions: transitionSuggestion(state.suggestions, action.locator, 'pending', 'dismissed'),
      };
    case UNDISMISS:
      return {
        ...state,
        suggestions: transitionSuggestion(state.suggestions, action.locator, 'dismissed', 'pending'),
      };
    case APPLY_ALL:
      // Caller (`applyAll` in the provider) computed `acceptAllPending`
      // already. We trust that payload here rather than recompute,
      // keeping a single source of truth for the post-accept state.
      return { ...state, suggestions: action.suggestions };
    case SKIP_ENTRY_FOR_SESSION:
      return { ...state, isEntryAlertOpen: false, skippedForVisit: true };
    case REQUEST_DISMISS_ALL:
      return { ...state, isDismissAllConfirmOpen: true };
    case CANCEL_DISMISS_ALL:
      return { ...state, isDismissAllConfirmOpen: false };
    case CONFIRM_DISMISS_ALL:
      // Mirrors RESET_REVIEW but also closes the confirmation modal in the
      // same dispatch so the modal doesn't linger for one render cycle.
      return {
        ...state,
        suggestions: emptySuggestionsState(),
        isReviewing: false,
        validation: undefined,
        isDismissAllConfirmOpen: false,
      };
    case DISMISS_NO_RESULTS_ALERT:
      return { ...state, noResultsAlertOpen: false };
    case RESET_FOR_TRAVELER:
      return initialState;
    default:
      return state;
  }
};

const useSuggestionsReducer = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return [state, dispatch] as [SuggestionsStateProps, Dispatch<Action>];
};

export default useSuggestionsReducer;
